/**
 * Utility functions for formatting data
 */

/**
 * Formats a number by adding spaces as thousands separators
 * @param number - The number to format (can be number or string)
 * @returns The formatted number as a string with space separators
 */
export const formatNumber = (number: number | string): string => {
  if (typeof number !== "number" && typeof number !== "string") {
    return String(number);
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

/**
 * Formats a price with currency symbol
 * @param price - The price to format
 * @param currency - The currency symbol (default: "₽")
 * @returns The formatted price string
 */
export const formatPrice = (price: number, currency: string = "₽"): string => {
  return `${formatNumber(price)} ${currency}`;
};

/**
 * Formats a date string to a readable format
 * @param dateString - The date string to format
 * @param options - Intl.DateTimeFormat options
 * @returns The formatted date string
 */
export const formatDate = (
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }
): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', options);
  } catch {
    return dateString;
  }
};

/**
 * Formats a file size in bytes to a human-readable format
 * @param bytes - The size in bytes
 * @returns The formatted size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};